import { memo, useState } from 'react';
import { Users, Globe } from 'lucide-react';
import { DELIVERY_TYPE_COLORS } from '../shared/constants';
import { ChartWrapper } from '../shared/ChartWrapper';
import { usePartnershipData } from './usePartnershipData.jsx';

export const PartnerLandscapeChart = memo(function PartnerLandscapeChart() {
  const { partnerStats, countriesWithPartners, localFilteredResults } = usePartnershipData();
  const [hoveredType, setHoveredType] = useState(null);

  if (!partnerStats || partnerStats.total === 0) return null;

  const deliveryTypes = Object.entries(partnerStats.byDeliveryType)
    .sort((a, b) => b[1] - a[1]);

  const chartData = deliveryTypes.map(([type, count]) => ({
    delivery_type: type,
    partner_count: count,
    percentage: partnerStats.total > 0 ? Math.round((count / partnerStats.total) * 100) : 0,
  }));

  return (
    <ChartWrapper title="Partnership Landscape" info="Snapshot of the partnership ecosystem: how many unique external partners engage in each delivery role (Demand, Innovation, Scaling). Each partner is counted once regardless of how many results they contribute to." sourceResults={localFilteredResults} chartData={chartData} exportFilename="partnership-landscape" description="Counts unique partners by delivery role. A partner appearing in multiple results is counted once. See Partner Network Overview for result-level engagement counts.">
      <p className="text-sm text-gray-500 -mt-1 mb-2">Each bar shows how many unique partners serve each delivery role</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24, padding: '8px 0' }}>
        {/* Stat boxes */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: 16, backgroundColor: '#f8fafc', borderRadius: 8 }}>
            <div style={{ width: 48, height: 48, backgroundColor: '#007DBC', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
              <Users size={24} />
            </div>
            <div>
              <p style={{ fontSize: 24, fontWeight: 700, color: '#0f172a', margin: 0 }}>{partnerStats.total}</p>
              <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>Total Partners</p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: 16, backgroundColor: '#f8fafc', borderRadius: 8 }}>
            <div style={{ width: 48, height: 48, backgroundColor: '#F37021', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
              <Globe size={24} />
            </div>
            <div>
              <p style={{ fontSize: 24, fontWeight: 700, color: '#0f172a', margin: 0 }}>{countriesWithPartners}</p>
              <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>Countries with Partners</p>
            </div>
          </div>
        </div>

        {/* Delivery type bars */}
        <div>
          <h5 style={{ fontSize: 13, fontWeight: 500, color: '#475569', marginBottom: 12 }}>Partners by Delivery Type</h5>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {deliveryTypes.map(([type, count]) => {
              const pct = partnerStats.total > 0 ? Math.round((count / partnerStats.total) * 100) : 0;
              return (
                <div
                  key={type}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'default' }}
                  onMouseEnter={() => setHoveredType(type)}
                  onMouseLeave={() => setHoveredType(null)}
                >
                  <div style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: DELIVERY_TYPE_COLORS[type] || DELIVERY_TYPE_COLORS['Other'], flexShrink: 0 }} />
                  <div style={{ width: 120, fontSize: 13, color: '#475569', flexShrink: 0 }}>{type}</div>
                  <div style={{ flex: 1, height: 24, backgroundColor: '#f1f5f9', borderRadius: 12, overflow: 'hidden', position: 'relative' }}>
                    {[10,20,30,40,50,60,70,80,90].map(tick => (
                      <div key={tick} style={{ position: 'absolute', top: 0, bottom: 0, width: 1, backgroundColor: 'rgba(203,213,225,0.4)', left: `${tick}%` }} />
                    ))}
                    <div
                      style={{
                        height: '100%',
                        borderRadius: 12,
                        width: `${partnerStats.total > 0 ? (count / partnerStats.total) * 100 : 0}%`,
                        backgroundColor: DELIVERY_TYPE_COLORS[type] || DELIVERY_TYPE_COLORS['Other'],
                        opacity: hoveredType && hoveredType !== type ? 0.4 : 1,
                        transition: 'width 0.3s ease, opacity 0.15s',
                        position: 'relative',
                      }}
                    />
                  </div>
                  <div style={{ width: 80, fontSize: 13, fontWeight: 500, color: '#0f172a', textAlign: 'right', whiteSpace: 'nowrap', flexShrink: 0 }}>{count} ({pct}%)</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Distribution segment bar */}
        {deliveryTypes.length > 0 && (
          <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 16 }}>
            <h5 style={{ fontSize: 13, fontWeight: 500, color: '#475569', marginBottom: 8 }}>Distribution by Delivery Type</h5>
            <div style={{ height: 24, width: '100%', borderRadius: 12, overflow: 'hidden', display: 'flex' }}>
              {deliveryTypes.map(([type, count]) => {
                const pct = partnerStats.total > 0 ? (count / partnerStats.total) * 100 : 0;
                return (
                  <div
                    key={type}
                    style={{
                      height: '100%',
                      width: `${pct}%`,
                      backgroundColor: DELIVERY_TYPE_COLORS[type] || DELIVERY_TYPE_COLORS['Other'],
                      transition: 'width 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    title={`${type}: ${count} partners (${Math.round(pct)}%)`}
                  >
                    {pct >= 8 && <span style={{ fontSize: 10, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap' }}>{Math.round(pct)}%</span>}
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 8 }}>
              {deliveryTypes.map(([type]) => (
                <div key={type} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: DELIVERY_TYPE_COLORS[type] || DELIVERY_TYPE_COLORS['Other'] }} />
                  <span style={{ fontSize: 12, color: '#64748b' }}>{type}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </ChartWrapper>
  );
});
